import { FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { Capabilities } from '@/lib/api/types.gen';
import { useQuery } from '@tanstack/react-query';
import { PropsWithChildren, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

type CameraFormValues = {
  name: string;
  description: string;
  monitored: boolean;
  quillNotes: string;
  activationWkt?: string;
  capabilities: Capabilities;
};

export const CameraFormProvider = (
  props: PropsWithChildren<{ deviceId: string; cameraId: string }>
) => {
  const { deviceId, cameraId } = props;
  const subDevicesQuery = useQuery(FetchSubDevicesQuery({ deviceId }));
  const subDevices = subDevicesQuery.data ?? [];
  const camera = subDevices.find((c) => c.id === cameraId);

  const form = useForm<CameraFormValues>({
    defaultValues: {
      name: '',
      description: '',
      monitored: false,
      quillNotes: '',
      activationWkt: '',
      capabilities: {
        canLivestream: false,
        canTalkdown: false,
        canPlayback: false,
      },
    },
  });

  useEffect(() => {
    if (camera == null) return;

    form.reset({
      name: camera.name ?? undefined,
      description: camera.description ?? undefined,
      monitored: camera.monitored ?? false,
      quillNotes: camera.quillNotes ?? undefined,
      activationWkt: camera.activationWkt ?? undefined,
      capabilities: camera.capabilities ?? {},
    });
  }, [camera]);

  return <FormProvider {...form}>{props.children}</FormProvider>;
};

export const useCameraFormCtx = () => useFormContext<CameraFormValues>();
